import React from 'react';
import { Link } from 'react-router-dom';
import RecommendationCardSkeleton from './RecommendationCardSkeleton';

/**
 * DashboardRecommendationsSection
 *
 * Props:
 * - recommendationsLoading: boolean
 * - recommendationsError: string | null
 * - topRecommendations: array
 * - handleAddToProject: function(rec)
 */
const DashboardRecommendationsSection = ({
  recommendationsLoading,
  recommendationsError,
  topRecommendations,
  handleAddToProject
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Quick-Win Energy Efficiency Opportunities
          </h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Based on your industry
          </span>
        </div>
      </div>
      <div className="p-6">
        {recommendationsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <RecommendationCardSkeleton key={i} />
            ))}
          </div>
        ) : recommendationsError ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <div className="text-orange-600 dark:text-orange-400 mb-2">
              ⚠️ {recommendationsError}
            </div>
            <p>Complete an assessment to see personalized recommendations.</p>
          </div>
        ) : topRecommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topRecommendations.map((rec) => (
              <div
                key={rec.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      rec.priority === 'high'
                        ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                        : rec.priority === 'medium'
                        ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                        : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    }`}
                  >
                    {rec.priority?.toUpperCase() || 'MEDIUM'}
                  </span>
                  {rec.category && (
                    <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                      {rec.category}
                    </span>
                  )}
                </div>

                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {rec.title}
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
                  {rec.description}
                </p>

                <div className="space-y-1 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Cost:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${rec.implementation_cost_aud?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Annual Savings:
                    </span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      ${rec.annual_savings_aud?.toLocaleString()}/yr
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Payback:</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      {rec.payback_years} years
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">ROI:</span>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">
                      {rec.roi_percent}%
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => handleAddToProject(rec)}
                  className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200"
                >
                  Add to Project
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Complete an assessment to see personalized energy efficiency recommendations.
            <div className="mt-2">
              <Link
                to="/assessment/self-guided"
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
              >
                Start Assessment →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardRecommendationsSection;


